// Added by Sejune Oh on 2019-03-12
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ElementTimeStampProps } from '@/helper/define'
import Intl from '@/helper/Intl'
import { getQueryPath } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root store
import Rootstore from '../../index'

// Store
import {
  reqGetNode,
  reqGetNodes,
  reqCreateNode,
  reqDeleteNode,
  reqUpdateNode,
  reqDetectAvailableNode,
  reqAppSessionCapa,
} from './NodeStore.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface NodeElementProps extends ElementTimeStampProps {
  id?: string
  name?: string
  description?: string
  node_key?: string
  activated?: boolean
  app_session_enabled?: boolean
  aps_session_enabled?: boolean
  score?: NodeScoreType
  gpu_indexes?: string
  trtis_endpoint?: string
  connected?: boolean
  ip_address?: string
  max_threads_per_session?: number
  can_run_multiple_aps_engines?: boolean
  running_schedule?: string
  running_schedule_enabled?: boolean
}

export interface DisplableNodeElementProps extends NodeElementProps {
  node_connected?: string
  ip_address?: string
}

export interface GetCapascoresArgProps {
  connected_only: boolean
  site_agent_only: boolean
  app_session_only: boolean
  aps_session_only: boolean
}

export interface GetAvailableNodeParam {
  required_score_cpu?: number
  app_session_only: boolean
  client_key: string
}

export interface GetAvailableNodeArgProps {
  required_score_cpu?: number
  required_score_gpu?: number
  app_session_only?: boolean
  aps_session_only?: boolean
  client_key?: string
}

export type NodeScoreType = {
  cpu: number
  gpu: number
}

export type AdvancedNodeProps = {
  importNode: NodeElementProps
  dataNode: NodeElementProps
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class NodeStore {
  _totalCount: number = 0
  _apsSessionAvailable: boolean = false
  _elements: Array<NodeElementProps | DisplableNodeElementProps> = []

  root?: Rootstore

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: Rootstore) {
    this.root = root
    makeAutoObservable(this)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get elements() {
    return this._elements
  }

  get totalCount() {
    return this._totalCount
  }

  get apsSessionAvailable() {
    return this._apsSessionAvailable
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this._totalCount = count
  }

  setElements = (elements: Array<NodeElementProps>) => {
    this._elements = elements
  }

  setNodeConnected = (elements: Array<NodeElementProps>) => {
    const cntText = Intl.getIntlText({ id: 'htmljs_react.global.nodestore.msg_connected' })
    const disCntText = Intl.getIntlText({ id: 'htmljs_react.global.nodestore.msg_disconnected' })
    const disabledText = Intl.getIntlText({ id: 'htmljs_react.global.nodestore.msg_disabled' })

    this._elements = elements.map((node) => ({
      ...node,
      node_connected: !node.activated ? disabledText : node.connected ? cntText : disCntText,
      ip_address: node.ip_address || '-',
    }))
  }

  setAPSSessionAvailableStatus(status: boolean) {
    this._apsSessionAvailable = status
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElement = async (id: string) => {
    const { data: rtnElement } = await reqGetNode(id)
    return rtnElement
  }

  getElements = async (path: string) => {
    const {
      data: { total_count, items },
    } = await reqGetNodes(path)
    this._setTotalCount(parseInt(total_count!))
    return items!
  }

  addElement = async (element: NodeElementProps) => {
    const { data: rtnElement } = await reqCreateNode(element)
    return rtnElement
  }

  removeElement = async (id: string) => {
    return await reqDeleteNode(id)
  }

  updateElement = async (id: string, element: NodeElementProps) => {
    const { data: updatedElement } = await reqUpdateNode(id, element)
    return updatedElement.id
  }

  getAvailableNode = async ({
    required_score_cpu,
    app_session_only = true,
    client_key,
  }: GetAvailableNodeArgProps) => {
    const requiredScore = required_score_cpu ? `required_score.cpu=${required_score_cpu}` : ''
    const path = `${requiredScore}&app_session_only=${app_session_only.toString()}&client_key=${client_key}`
    return await reqDetectAvailableNode(path)
  }

  getUserSessionCapa = async () => {
    const {
      data: { license_max, node_capa_max, cur },
    } = await reqAppSessionCapa()
    return {
      curUserSessionCount: cur || 0,
      maxUserSessionCount: Math.min(license_max || 0, node_capa_max || 0),
      licensedMaxUserSessionCount: license_max || 0,
      nodeCapaMaxUserSessionCount: node_capa_max || 0,
    }
  }

  checkDuplicatedNodeName = async (nodeName: string) => {
    const filter = { $op: 'and', name: nodeName }
    const path = getQueryPath({ pageIndex: 0, orderBy: 'name', filter: JSON.stringify(filter) })

    const elements = await this.getElements(path)
    return elements.length > 0
  }

  checkDuplicatedNodeKey = async (nodeKey: string) => {
    const filter = { $op: 'and', node_key: nodeKey }
    const path = getQueryPath({ pageIndex: 0, orderBy: 'node_key', filter: JSON.stringify(filter) })

    const elements = await this.getElements(path)
    return elements.length > 0
  }
}
